import maplibregl, { type Map as MapLibreMap } from "maplibre-gl";
import { embedMapFitInsets } from "../map-embed-measure.ts";
import {
  createMapStreetViewButton,
  mapControlButton,
  mapZoomGroup,
  MINUS_ICON,
  PLUS_ICON,
} from "../map-embed-controls.ts";
import { VECTOR_STYLE_URL, vectorAttributionHtml } from "../../shared/basemap.ts";
import type { RouteFillFeature, RoutePaintFeature } from "../../shared/quest-route-paint.ts";
import type { LngLat } from "../../shared/types.ts";
import {
  applyStyledRouteLineScaleForContainer,
  setStyledRouteData,
  styledRouteLayerIds,
} from "./styled-route-layers.ts";
import { MapBasemap } from "../basemap.ts";
import { t } from "../i18n/index.ts";
import { registerStreetViewMap } from "../street-view/register.ts";
import type { QuestSpot } from "./types.ts";
import { questSpotMapPinForSpotTrace } from "./quest-spot-map-layers.ts";
import {
  QUEST_SPOT_DOM_FIT_MAX_ZOOM,
  QuestSpotDomMarkers,
} from "./quest-spot-dom-markers.ts";
import {
  spotQuestMapFitView,
} from "../../shared/quest-spot-map-fit.ts";
import { questSpotDomMarkerLabelsNudgeOffset, questSpotDomMarkerPhotoCenterOffset } from "./spot-dom-marker-fit.ts";

const QUEST_LAYER_IDS = styledRouteLayerIds("quest-map");
const ZOOM_MS = 250;
const QUEST_FALLBACK_COLOR = "#1a1a1a";

export interface QuestMapHandlers {
  onAddSpot?: (lat: number, lng: number) => void;
  onMoveSpot?: (index: number, lat: number, lng: number) => void;
}

/** Small embedded MapLibre map for the quest page (route line + numbered spots). */
export class QuestMap {
  private map: MapLibreMap;
  private spotMarkers = new QuestSpotDomMarkers();
  private line: LngLat[] | null = null;
  private editable: boolean;
  private handlers: QuestMapHandlers;
  private basemap: MapBasemap;
  private zoomIn: HTMLButtonElement;
  private zoomOut: HTMLButtonElement;
  private credit: HTMLElement;
  private resizeObserver: ResizeObserver;
  private lastSpots: QuestSpot[] = [];

  constructor(container: HTMLElement, editable: boolean, handlers: QuestMapHandlers = {}) {
    this.editable = editable;
    this.handlers = handlers;
    this.map = new maplibregl.Map({
      container,
      style: VECTOR_STYLE_URL,
      center: [126.978, 37.5665],
      zoom: 12,
      attributionControl: false,
      cooperativeGestures: true,
      pitchWithRotate: false,
      dragRotate: false,
    });
    this.credit = document.createElement("div");
    this.credit.className = "route-map-credit";
    this.credit.innerHTML = vectorAttributionHtml();
    container.appendChild(this.credit);

    this.zoomOut = mapControlButton("route-map-zoom-btn", t("rt.map.zoomOut"), MINUS_ICON, () => this.zoomBy(-1));
    this.zoomIn = mapControlButton("route-map-zoom-btn", t("rt.map.zoomIn"), PLUS_ICON, () => this.zoomBy(1));
    const zoom = mapZoomGroup(this.zoomOut, this.zoomIn);
    const streetView = createMapStreetViewButton();
    registerStreetViewMap({
      map: this.map,
      button: streetView,
      getCenter: () => {
        const c = this.map.getCenter();
        return { lng: c.lng, lat: c.lat };
      },
    });
    const primary = document.createElement("div");
    primary.className = "route-map-toolbar-primary";
    primary.append(streetView, zoom);
    const toolbarEnd = document.createElement("div");
    toolbarEnd.className = "route-map-toolbar-end";
    const toolbar = document.createElement("div");
    toolbar.className = "route-map-toolbar";
    toolbar.append(primary, toolbarEnd);
    container.appendChild(toolbar);

    this.basemap = new MapBasemap({
      map: this.map,
      creditEl: this.credit,
      onStyleReady: () => {
        this.setLine(this.line);
        if (this.lastSpots.length) this.paintSpots(this.lastSpots);
      },
    });
    primary.insertBefore(this.basemap.getToggleElement(), zoom);
    void this.basemap.boot();

    this.map.on("zoom", () => this.paintZoom());
    this.paintZoom();

    if (editable) {
      this.map.on("click", (e) => {
        if ((e.originalEvent.target as HTMLElement | null)?.closest?.(".maplibregl-marker")) return;
        this.handlers.onAddSpot?.(e.lngLat.lat, e.lngLat.lng);
      });
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.map.resize();
      this.fit();
      const box = this.map.getContainer();
      applyStyledRouteLineScaleForContainer(this.map, QUEST_LAYER_IDS, box.clientWidth, box.clientHeight);
    });
    this.resizeObserver.observe(container);
  }

  setLine(
    line: LngLat[] | null,
    paint: RoutePaintFeature[] | null = null,
    fill: RouteFillFeature[] | null = null,
  ): void {
    this.line = line;
    const paintLine = () => {
      setStyledRouteData(this.map, QUEST_LAYER_IDS, paint, line, QUEST_FALLBACK_COLOR, fill);
      const box = this.map.getContainer();
      applyStyledRouteLineScaleForContainer(this.map, QUEST_LAYER_IDS, box.clientWidth, box.clientHeight);
      this.basemap.reconcileLayerStack();
      this.fit();
      applyStyledRouteLineScaleForContainer(this.map, QUEST_LAYER_IDS, box.clientWidth, box.clientHeight);
    };
    if (this.map.isStyleLoaded()) paintLine();
    else this.map.once("load", paintLine);
  }

  setSpots(spots: QuestSpot[]): void {
    this.lastSpots = spots;
    this.paintSpots(spots);
    this.fit();
  }

  private paintSpots(spots: QuestSpot[]): void {
    const pins = spots.map((spot, index) => questSpotMapPinForSpotTrace(spot, index));
    this.spotMarkers.sync(this.map, pins, {
      draggable: this.editable,
      onMove: (index, lat, lng) => this.handlers.onMoveSpot?.(index, lat, lng),
    });
  }

  private zoomBy(dir: 1 | -1): void {
    const next = this.map.getZoom() + dir;
    const zoom = Math.min(this.map.getMaxZoom(), Math.max(this.map.getMinZoom(), next));
    this.map.easeTo({ zoom, duration: ZOOM_MS });
  }

  private paintZoom(): void {
    const zoom = this.map.getZoom();
    this.zoomIn.disabled = zoom >= this.map.getMaxZoom();
    this.zoomOut.disabled = zoom <= this.map.getMinZoom();
  }

  private fit(): void {
    const spotCoords: LngLat[] = this.lastSpots.map((s) => [s.lng, s.lat]);
    const hasLine = Boolean(this.line?.length);
    if (!hasLine && spotCoords.length >= 1) {
      const box = this.map.getContainer();
      const inset = embedMapFitInsets(box);
      const pins = this.lastSpots.map((s) => ({ lng: s.lng, lat: s.lat, kind: s.kind ?? null }));
      const view = spotQuestMapFitView(pins, 0, box.clientWidth, box.clientHeight, inset);
      if (view) {
        this.map.jumpTo(view);
        requestAnimationFrame(() => {
          const off = questSpotDomMarkerPhotoCenterOffset(box);
          if (off && (Math.abs(off[0]) > 1 || Math.abs(off[1]) > 1)) {
            try {
              this.map.panBy(off, { duration: 0 });
            } catch {
              /* ignore */
            }
          }
          const nudge = questSpotDomMarkerLabelsNudgeOffset(box);
          if (nudge) {
            try {
              this.map.panBy(nudge, { duration: 0 });
            } catch {
              /* ignore */
            }
          }
        });
      }
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    let any = false;
    if (this.line) {
      for (const p of this.line) {
        bounds.extend(p);
        any = true;
      }
    }
    for (const p of spotCoords) {
      bounds.extend(p);
      any = true;
    }
    if (!any) return;
    const box = this.map.getContainer();
    const inset = embedMapFitInsets(box);
    const pad = 48;
    this.map.fitBounds(bounds, {
      padding: {
        top: Math.max(inset.top, pad),
        right: Math.max(inset.right, pad),
        bottom: Math.max(inset.bottom, pad),
        left: Math.max(inset.left, pad),
      },
      maxZoom: QUEST_SPOT_DOM_FIT_MAX_ZOOM,
      duration: 0,
    });
  }

  destroy(): void {
    this.resizeObserver.disconnect();
    this.spotMarkers.clear();
    this.map.remove();
  }
}
